import { useState } from "react";
import { IconButton, Tooltip } from "@material-ui/core";
import Api from "lib/api/httpRequestHelper";
import {
  columnTextField,
  columnNumberField,
  columnPriceField,
  chaveNFFormatter,
} from "utils/tables-utils";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import DialogNotaFiscalDetails from "./dialog-nota-fiscal-details";
import DialogOcorrenciasTransporte from "./dialog-ocorrencias-transporte";
import {
  LocalShipping,
  FindInPage,
  GetApp,
  Visibility,
} from "@mui/icons-material";

const getFileInvoice = async (tipo, chave) => {
  return await Api.get(tipo, "invoice", "?chaveNf=" + chave, false)
    .then((value) => {
      const url =
        "data:application/octet-stream;base64," + value.Result.FileBase64;
      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.target = "_blank";
      downloadLink.download = chave + "." + tipo;
      downloadLink.click();
    })
    .catch((error) => {
      console.log(error);
    });
};

export default function TableNotasFiscais({ data, totalRegistros, loading, extraActions }) {
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ocorrenciasDialogOpen, setOcorrenciasDialogOpen] = useState(false);
  const [selectedRowForOcorrencias, setSelectedRowForOcorrencias] =
    useState(null);

  const handleOpenDialog = (row) => {
    setSelectedInvoice(row);
    setDialogOpen(true);
  };
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedInvoice(null);
  };

  const handleOpenOcorrenciasDialog = (row) => {
    setSelectedRowForOcorrencias(row);
    setOcorrenciasDialogOpen(true);
  };

  const handleCloseOcorrenciasDialog = () => {
    setOcorrenciasDialogOpen(false);
    setSelectedRowForOcorrencias(null);
  };

  const columnModel = [
    columnNumberField({
      dataField: "NumeroNf",
      text: "Número",
    }),
    columnTextField({
      dataField: "DataEmissao",
      text: "Data Emissão",
      align: "center",
    }),
    columnTextField({
      dataField: "Status",
      text: "Status",
      align: "center",
    }),
    columnPriceField({
      dataField: "ValorTotal",
      text: "Valor Liquido",
    }),
    columnTextField({
      dataField: "Entregue",
      text: "Entregue",
      align: "center",
      formatter: (cell) => {
        if (cell === true || cell === "true" || cell === "Sim") return "Sim";
        if (cell === false || cell === "false" || cell === "Não") return "Não";
        return cell ?? "-";
      },
    }),
    columnTextField({
      dataField: "DataPrevisaoEntrega",
      text: "Previsão Entrega",
      align: "center",
      formatter: (cell) => cell || "-",
    }),
    columnTextField({
      dataField: "NrProposta",
      text: "Proposta",
      align: "center",
      formatter: (cell) => cell ?? "-",
    }),
    columnNumberField({
      dataField: "NrPedido",
      text: "Pedido",
    }),
    columnTextField({
      dataField: "Marca",
      text: "Marca",
    }),
    columnTextField({
      dataField: "CCLoja",
      text: "CC Loja",
      formatter: (cell) => cell || "-",
    }),
    columnTextField({
      dataField: "CondicaoPagamento",
      text: "Condição Pagamento",
      formatter: (cell) => cell || "-",
    }),
    columnTextField({
      dataField: "ChaveNfe",
      text: "Chave da NF",
      align: "center",
      formatter: (_, row) => {
        return chaveNFFormatter(row.ChaveNfe);
      },
    }),
    columnTextField({
      dataField: "rastreio",
      text: "Rastreio",
      align: "center",
      formatter: (_, row) => (
        <Tooltip title="Ver Ocorrências de Entrega">
          <IconButton
            size="small"
            onClick={() => handleOpenOcorrenciasDialog(row)}
            disabled={!row?.ChaveNfe}
          >
            <LocalShipping fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    }),
    columnTextField({
      dataField: "consulta",
      text: "Consulta Sefaz",
      align: "center",
      formatter: (_, row) => (
        <Tooltip title="Consultar Sefaz">
          <IconButton
            onClick={() => {
              window.open(
                "https://www.nfe.fazenda.gov.br/portal/consultaRecaptcha.aspx?tipoConsulta=resumo&nfe=" +
                  row.ChaveNfe,
                "_blank"
              );
            }}
            size="small"
          >
            <FindInPage fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    }),
    columnTextField({
      dataField: "XmlNoS3",
      text: "XML",
      align: "center",
      formatter: (_, row) => (
        <Tooltip title="Download XML">
          <IconButton
            size="small"
            onClick={(e) => getFileInvoice("xml", row.ChaveNfe)}
          >
            <GetApp fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    }),
    columnTextField({
      dataField: "PdfNoS3",
      text: "PDF",
      align: "center",
      formatter: (_, row) => (
        <Tooltip title="Download PDF">
          <IconButton
            size="small"
            onClick={(e) => getFileInvoice("pdf", row.ChaveNfe)}
          >
            <GetApp fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    }),
    columnTextField({
      dataField: "detalhes",
      text: "Detalhes",
      align: "center",
      formatter: (_, row) => (
        <Tooltip title="Ver Detalhes">
          <IconButton size="small" onClick={() => handleOpenDialog(row)}>
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    }),
  ];

  return (
    <>
      <ToolkitTable
        dataTable={data}
        columnModel={columnModel}
        keyField="NumeroNf"
        showPrint
        extraActions={extraActions}
        pagination={{
          options: [10, 25, 50, 100],
          defaultOption: 10,
          totalSize: totalRegistros,
          showTotal: true,
        }}
        loading={loading}
        style={{
          boxShadow: "none",
          margin: "0 -1rem",
        }}
      />
      <DialogNotaFiscalDetails
        open={dialogOpen}
        onClose={handleCloseDialog}
        invoice={selectedInvoice}
      />
      <DialogOcorrenciasTransporte
        open={ocorrenciasDialogOpen}
        onClose={handleCloseOcorrenciasDialog}
        chaveNf={selectedRowForOcorrencias?.ChaveNfe}
        numero={selectedRowForOcorrencias?.NumeroNf}
        serie={selectedRowForOcorrencias?.SerieNf}
      />
    </>
  );
}
